import React, { useRef } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import ScrollFadeWrapper from '../ScrollFadeWrapper/ScrollFadeWrapper';
import Image from '../Image/Image';
import { toSentenceCase } from '../../utils/casing';

const DoubleArrowIconSvg =
  'https://assets.askhippo.co.za/Icons/common/DoubleArrow_White.svg';

const HippoSubLineGenericComponent = ({
  route,
  imageDirection = 'left',
  color = 'white',
  layout = 'default',
  title,
  buttonText,
  subTitle,
  image = 'HippoTickle',
  buttonColor,
}) => {
  const navigate = useNavigate();
  const buttonRef = useRef(null);
  // Bounce animation that resolves when complete
  const gs_bounce = (target, strength = 0.85) => {
    if (!target) return Promise.resolve();
    return new Promise((resolve) => {
      const tl = gsap.timeline({
        onComplete: resolve,
        defaults: { transformOrigin: '50% 50%' },
      });
      tl.to(target, {
        scale: strength,
        duration: 0.15,
        ease: 'power3.inOut',
      }).to(target, {
        scale: 1,
        duration: 0.85,
        ease: 'elastic.out(1.75, 0.4)',
      });
    });
  };

  const handleClick = async () => {
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!prefersReduced) {
      await gs_bounce(buttonRef.current);
    }
    navigate(route);
    window.scrollTo(0, 0);
  };

  const isRight = imageDirection === 'right';
  const isCompact = layout === 'compact';

  // Determine color styles
  const backgroundColor =
    color === 'grey'
      ? '#E3E6E8'
      : color === 'blue'
        ? '#162F3B'
        : color === 'blue-white'
          ? '#162F3B'
          : '#FFFFFF';
  const textColor =
    color === 'blue'
      ? '#C7CA00'
      : color === 'blue-white'
        ? '#FFFFFF'
        : '#162F3B';

  // 🦛 Image mapping
  const BASE = 'https://assets.askhippo.co.za/banners/desktop/';

  const imageMap = {
    HippoArmsCrossed: {
      src: BASE + 'HippoArmsCrossed.svg',
      alt: 'Hippo mascot with arms crossed confidently',
      title: 'Hippo helps you compare quotes with confidence',
    },

    HippoDontKnow: {
      src: BASE + 'HippoDontKnow.svg',
      alt: 'Hippo mascot shrugging in confusion',
      title: 'Not sure which insurance to choose? Hippo can help.',
    },
    HippoEyeCoverOhNo: {
      src: BASE + 'HippoEyeCoverOhNo.svg',
      alt: 'Hippo mascot covering eyes in worry',
      title: 'Don’t stress — Hippo compares quotes for you',
    },
    HippoGottcha: {
      src: BASE + 'HippoGottcha.svg',
      alt: 'Hippo mascot saying gottcha',
      title: 'Not sure which insurance to choose? Hippo can help.',
    },
    HippoHandsDown: {
      src: BASE + 'HippoHandsDown.svg',
      alt: 'Hippo mascot with hands down',
      title: 'Hippo – your friendly insurance comparison helper',
    },
    HippoHandSide: {
      src: BASE + 'HippoHandSide.svg',
      alt: 'Hippo mascot gesturing to the side',
      title: 'Explore insurance options with Hippo’s help',
    },
    HippoHandSideTiltFace: {
      src: BASE + 'HippoHandSideTiltFace.svg',
      alt: 'Hippo mascot gesturing with a tilted face',
      title: 'Hippo guides you to better insurance choices',
    },
    HippoHandsUp: {
      src: BASE + 'HippoHandsUp.svg',
      alt: 'Hippo mascot with hands up',
      title: 'Hippo helps you compare car insurance easily',
    },
    HippoHandUp: {
      src: BASE + 'HippoHandUp.svg',
      alt: 'Hippo mascot raising its hand',
      title: 'Hippo is ready to help you compare insurance quotes',
    },
    HippoOhNo: {
      src: BASE + 'HippoOhNo.svg',
      alt: 'Hippo mascot looking worried',
      title: 'Unexpected costs? Hippo helps you find cover fast.',
    },
    HippoPhew: {
      src: BASE + 'HippoPhew.svg',
      alt: 'Hippo mascot relieved',
      title: 'Phew! Hippo makes comparing insurance easy.',
    },
    HippoThink: {
      src: BASE + 'HippoThink.svg',
      alt: 'Hippo mascot thinking',
      title: 'Thinking about insurance? Hippo compares for you.',
    },
    HippoThinkTilt: {
      src: BASE + 'HippoThinkTilt.svg',
      alt: 'Hippo mascot thinking with a tilted head',
      title: 'Hippo helps you decide on the best insurance option',
    },
    HippoThisWay: {
      src: BASE + 'HippoThisWay.svg',
      alt: 'Hippo mascot showing the direction',
      title: 'Thinking about insurance? Hippo compares for you.',
    },
    HippoTickle: {
      src: BASE + 'HippoTickle.svg',
      alt: 'Hippo mascot smiling and playful',
      title: 'Save time with Hippo’s car insurance comparison',
    },
    HippoBannerChamp: {
      src: BASE + 'HippoBannerChamp.svg',
      alt: 'Hippo mascot banner champ',
      title: 'Hippo banner champ',
    },
  };

  // Choose image based on prop (fallback to HippoTickle)
  const SelectedImage = imageMap[image] || imageMap.HippoTickle;
  const contentShift = 0;
  const titleText = typeof title === 'string' ? title : '';
  const subTitleText = typeof subTitle === 'string' ? subTitle : '';
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '1200px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',

        boxSizing: 'border-box',
      }}
    >
      {/* Main Card */}
      <ScrollFadeWrapper sx={{ width: '100%' }}>
        <Box
          sx={{
            width: '100%',
            backgroundColor: backgroundColor,
            borderRadius: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: isRight ? 'row-reverse' : 'row',
            padding: '40px',
            boxSizing: 'border-box',
          }}
        >
          {/* Image */}
          <ScrollFadeWrapper
            delay={0.15}
            startingOpacity={0.5}
            sx={{
              flexShrink: 0,
              width: isCompact ? '272px' : '300px',
              height: isCompact ? '272px' : '300px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Image
              src={SelectedImage.src}
              alt={SelectedImage.alt}
              title={SelectedImage.title}
              width={isCompact ? 272 : 300}
              height={isCompact ? 272 : 300}
              style={{ display: 'block' }}
            />
          </ScrollFadeWrapper>

          {/* Text + Button */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              flex: 1,
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: isCompact ? '680px' : '760px',
                px: isCompact ? '12px' : '8px',
                boxSizing: 'border-box',
                mb: isCompact ? '6px' : '10px',
              }}
            >
              <Typography
                component={'h3'}
                sx={{
                  fontFamily: 'Pally, Inter, sans-serif',
                  width: '100%',
                  fontWeight: 700,
                  fontSize: isCompact ? '4rem' : '4.5rem',
                  lineHeight: isCompact ? '3.75rem' : '4.25rem',
                  letterSpacing: '-0.03em',
                  textAlign: 'center',
                  color: textColor,
                  whiteSpace: 'pre-line',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {titleText}
              </Typography>
            </Box>

            <Box
              sx={{
                width: '100%',
                maxWidth: isCompact ? '660px' : '720px',
                px: isCompact ? '12px' : '16px',
                boxSizing: 'border-box',
              }}
            >
              <Typography
                component={'h6'}
                sx={{
                  fontFamily: 'Pally, Inter, sans-serif',
                  width: '100%',
                  fontWeight: 700,
                  fontSize: isCompact ? '1.625rem' : '2rem',
                  lineHeight: isCompact ? '1.875rem' : '2.25rem',
                  letterSpacing: '-0.03em',
                  textAlign: 'center',
                  color: textColor,
                  whiteSpace: 'pre-line',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {subTitleText}
              </Typography>
            </Box>

            <Button
              ref={buttonRef}
              variant="contained"
              disableElevation
              onClick={handleClick}
              sx={{
                mt: isCompact ? '24px' : '31px',
                height: '48px',
                padding: '12px 24px',
                ml: isRight ? 0 : `-${contentShift}px`,
                mr: isRight ? `-${contentShift}px` : 0,
                backgroundColor:
                  buttonColor === 'green' ? '#C7CA00' : '#FF0074',
                borderRadius: '999px',
                textTransform: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                boxSizing: 'border-box',
                willChange: 'transform',
                '&:hover': {
                  backgroundColor:
                    buttonColor === 'green' ? '#C7CA00' : '#FF0074',
                },
              }}
            >
              <Typography
                component={'a'}
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  lineHeight: '1.5rem',
                  letterSpacing: '-0.03em',
                  color: buttonColor === 'green' ? '#162F3B' : '#FFFFFF',
                  whiteSpace: 'nowrap',
                }}
              >
                {toSentenceCase(buttonText)}
              </Typography>
              <img
                src={DoubleArrowIconSvg}
                alt="Double arrow icon"
                width={24}
                height={24}
                style={{
                  width: '24px',
                  height: '24px',
                  marginLeft: '4px',
                  filter:
                    buttonColor === 'green'
                      ? 'brightness(0)'
                      : 'brightness(0) invert(1)',
                }}
              />
            </Button>
          </Box>
        </Box>
      </ScrollFadeWrapper>
    </Box>
  );
};

export default HippoSubLineGenericComponent;
